import React from "react";
import {
  ShieldCheck,
  Megaphone,
  Users,
  Leaf,
  ArrowUpRight,
} from "lucide-react";

const members = [
  {
    name: "Kalu Idika",
    role: "Chairperson",
    icon: ShieldCheck,
    description:
      "A seasoned technocrat with extensive experience in public sector leadership and policy implementation. He provides strategic direction to the Board and ensures full alignment of the Foundation’s work with its mission of advancing climate resilience, sustainable development, and long-term community impact.",
  },
  {
    name: "Chiedozie K. Idika",
    role: "Vice Chairman / Media & Communication Officer",
    icon: Megaphone,
    description:
      "Supports the Chairperson in providing strategic leadership and governance oversight for the Foundation. He also leads media and communication efforts, ensuring clear, consistent, and effective engagement with stakeholders, partners, and the public, while strengthening the Foundation’s visibility and messaging around climate resilience and community impact.",
  },
  {
    name: "Elizabeth S. Okpo",
    role: "Trustee",
    icon: Users,
    description:
      "An accomplished community development expert with over 35 years of experience working at grassroots and institutional levels. She brings deep knowledge of participatory development, community mobilization, and social inclusion, helping to ensure that the Foundation’s interventions remain people-centered and locally relevant.",
  },
  {
    name: "Abang C. B.",
    role: "Trustee / Agricultural Advisor",
    icon: Leaf,
    description:
      "An agriculture specialist and technical advisor with strong expertise in agricultural systems, rural development, and climate-smart farming practices. He provides technical guidance to strengthen the Foundation’s work in sustainable agriculture, food security, and climate adaptation strategies for farming communities.",
  },
];

const BoardOfTrustees = () => {
  return (
    <section className="relative overflow-hidden bg-[#F7F4EC] py-10 lg:py-20 px-6 lg:px-12">
      
      {/* Background Blur */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-green-400/10 blur-3xl rounded-full" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-400/10 blur-3xl rounded-full" />

      <div className="relative max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-10 lg:mb-20">
          
          <div className="max-w-3xl">

            <p className="uppercase tracking-[0.3em] text-green-400 text-xs mb-4">
              Members of Board
            </p>

            <h2 className="text-3xl md:text-5xl font-semibold text-[#1B1B1B] leading-tight">
              Board of Trustees
            </h2>

          </div>

        </div>

        {/* Members Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

          {members.map((member, index) => {
            const Icon = member.icon;

            return (
              <div
                key={index}
                className="group relative bg-white border border-[#ECE7DA] rounded-[32px] p-8 md:p-10 overflow-hidden hover:shadow-2xl transition-all duration-500"
              >

                {/* Hover Accent */}
                <div className="absolute top-0 left-0 h-1 w-0 bg-green-400 group-hover:w-full transition-all duration-500" />

                {/* Top */}
                <div className="flex items-start justify-between gap-4">

                  <div>

                    <p className="text-green-400 uppercase tracking-[0.15em] text-xs font-semibold mb-3">
                      {member.role}
                    </p>

                    <h3 className="text-xl md:text-3xl font-semibold text-[#1B1B1B] leading-snug">
                      {member.name}
                    </h3>

                  </div>

                  <div className="min-w-[72px] h-[72px] rounded-2xl bg-[#F7F4EC] flex items-center justify-center group-hover:bg-green-400 transition-all duration-500">
                    <Icon
                      size={32}
                      className="text-green-400 group-hover:text-[#0F172A] transition-all duration-500"
                    />
                  </div>

                </div>

                {/* Divider */}
                <div className="w-full h-[1px] bg-[#ECE7DA] my-8" />

                {/* Description */}
                <p className="text-[#555] leading-relaxed text-[15px] md:text-base">
                  {member.description}
                </p>

              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-10 lg:mt-24 bg-[#0F172A] rounded-[40px] p-10 md:p-16 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10 overflow-hidden relative">

          {/* Glow */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-green-400/10 blur-3xl rounded-full" />

          <div className="relative max-w-3xl">

            <p className="uppercase tracking-[0.3em] text-green-400 text-xs font-semibold mb-5">
              Take Action
            </p>

            <h3 className="text-lg md:text-xl text-white leading-tight mb-0 lg:mb-6">
              Climate action becomes meaningful when it changes lives on the ground.
              Join us in building resilient, sustainable communities.
            </h3>

          </div>

          <div className="relative flex flex-col sm:flex-row gap-4">

            <a
              href="https://www.linkedin.com/company/anna-foundations/"
              target="_blank"
              rel="noopener noreferrer"
              className="group border border-green-400 text-white hover:bg-green-400 hover:text-[#0F172A] px-8 py-4 rounded-full  transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              Donate Now

              <ArrowUpRight
                size={18}
                className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
              />
            </a>

            <a
              href="https://www.linkedin.com/company/anna-foundations/"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-green-400 hover:bg-white text-[#0F172A] px-8 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              Get Involved

              <ArrowUpRight
                size={18}
                className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
              />
            </a>

          </div>

        </div>

      </div>
    </section>
  );
};

export default BoardOfTrustees;